(ns capra.protocol
  "Wire protocol for capra — a Clojure-native capability RPC library.

   EDN tag literals:
     #rpc/ref 0            → result of op 0 in the current pipeline
     #rpc/ref [0 :name]    → (get-in (result-of-op-0) [:name])
     #rpc/cap \"cap-abc\"    → live server-side capability
     #rpc/fn  \"fn-xyz\"     → client-registered callback function

   Message flow:
     Client → Server: :rpc/call :rpc/release :rpc/ping
     Server → Client: :rpc/welcome :rpc/return :rpc/push :rpc/error :rpc/pong"
  (:require
   [malli.core :as m]))

;; =============================================================================
;; Records
;; =============================================================================

(defrecord Ref [op-idx path])
(defrecord Cap [id])
(defrecord FnRef [id])

;; =============================================================================
;; Reader Functions (installed via data_readers.cljc)
;; =============================================================================

(defn read-ref
  "Reader for #rpc/ref. Accepts integer or [integer & keys]."
  [form]
  (if (integer? form)
    (->Ref form nil)
    (->Ref (first form) (vec (rest form)))))

(defn read-cap
  "Reader for #rpc/cap — expects a string capability ID."
  [form]
  (->Cap form))

(defn read-fn
  "Reader for #rpc/fn — expects a string function ID."
  [form]
  (->FnRef form))

;; =============================================================================
;; Print Methods (EDN round-trip)
;; =============================================================================

#?(:clj
   (do
     (defmethod print-method Ref [^Ref r ^java.io.Writer w]
       (if (nil? (:path r))
         (.write w (str "#rpc/ref " (:op-idx r)))
         (.write w (str "#rpc/ref " (pr-str (into [(:op-idx r)] (:path r)))))))

     (defmethod print-method Cap [^Cap c ^java.io.Writer w]
       (.write w (str "#rpc/cap " (pr-str (:id c)))))

     (defmethod print-method FnRef [^FnRef f ^java.io.Writer w]
       (.write w (str "#rpc/fn " (pr-str (:id f)))))))

#?(:cljs
   (do
     (set! (.. Ref -prototype -cljs$core$IPrintWithWriter$_pr_writer$arity$3)
           (fn [r writer _opts]
             (if (nil? (:path r))
               (-write writer (str "#rpc/ref " (:op-idx r)))
               (-write writer (str "#rpc/ref " (pr-str (into [(:op-idx r)] (:path r))))))))

     (set! (.. Cap -prototype -cljs$core$IPrintWithWriter$_pr_writer$arity$3)
           (fn [c writer _opts]
             (-write writer (str "#rpc/cap " (pr-str (:id c))))))

     (set! (.. FnRef -prototype -cljs$core$IPrintWithWriter$_pr_writer$arity$3)
           (fn [f writer _opts]
             (-write writer (str "#rpc/fn " (pr-str (:id f))))))))

;; =============================================================================
;; Constructors & Predicates
;; =============================================================================

(defn make-ref
  "Construct a Ref. (make-ref 0) or (make-ref 0 :name :nested)"
  ([op-idx] (->Ref op-idx nil))
  ([op-idx & path] (->Ref op-idx (vec path))))

(defn cap [id] (->Cap id))

(defn fn-ref [id] (->FnRef id))

(defn ref? [x] (instance? Ref x))
(defn cap? [x] (instance? Cap x))
(defn fn-ref? [x] (instance? FnRef x))

(defn ref-with-path
  "Create a Ref that navigates into an op result.
   (ref-with-path 0 :quotes) → #rpc/ref [0 :quotes]"
  [op-idx & path]
  (->Ref op-idx (vec path)))

;; =============================================================================
;; Malli Schemas
;; =============================================================================

(def RefSchema [:fn {:error/message "expected #rpc/ref"} ref?])
(def CapSchema [:fn {:error/message "expected #rpc/cap"} cap?])
(def FnSchema  [:fn {:error/message "expected #rpc/fn"} fn-ref?])

(def OpKeyword :keyword)
(def CapId :string)
(def FnId :string)
(def QuestionId :int)

;; --- Pipeline Op ---

(def PipelineOp
  [:map
   [:op OpKeyword]
   [:args {:optional true} [:map-of :keyword :any]]
   [:cap  {:optional true} [:or CapSchema RefSchema]]])

;; --- Client → Server Messages ---

;; Removed messages — kept for reference if re-implemented later:
;; (def InitMessage
;;   [:map {:closed true}
;;    [:type    [:= :rpc/init]]
;;    [:version :string]
;;    [:auth    {:optional true} [:map-of :keyword :any]]])
;;
;; (def CancelMessage
;;   [:map {:closed true}
;;    [:type [:= :rpc/cancel]]
;;    [:qid  QuestionId]])

(def CallMessage
  [:map {:closed true}
   [:type   [:= :rpc/call]]
   [:qid    QuestionId]
   [:ops    [:vector {:min 1} PipelineOp]]
   [:return {:optional true} [:set :int]]])

(def ReleaseMessage
  [:map {:closed true}
   [:type [:= :rpc/release]]
   [:caps {:optional true} [:set CapId]]
   [:fns  {:optional true} [:set FnId]]])

(def PingMessage
  [:map {:closed true}
   [:type  [:= :rpc/ping]]
   [:token {:optional true} :string]])

(def ClientMessage
  [:multi {:dispatch :type}
   [:rpc/call    CallMessage]
   [:rpc/release ReleaseMessage]
   [:rpc/ping    PingMessage]])

;; --- Server → Client Messages ---

(def CapAdvertisement
  [:map
   [:id      CapId]
   [:methods [:set OpKeyword]]
   [:type    {:optional true} :keyword]
   [:ttl     {:optional true} :int]
   [:meta    {:optional true} :map]])

(def ReturnMessage
  [:map {:closed true}
   [:type      [:= :rpc/return]]
   [:qid       QuestionId]
   [:results   [:map-of :int :any]]
   [:caps      {:optional true} [:vector CapAdvertisement]]
   [:error-ops {:optional true} [:map-of :int
                                 [:map
                                  [:code    :keyword]
                                  [:message :string]
                                  [:data    {:optional true} :any]]]]])

(def WelcomeMessage
  [:map {:closed true}
   [:type        [:= :rpc/welcome]]
   [:session-cap CapSchema]
   [:caps        [:vector CapAdvertisement]]
   [:version     :string]])

(def PushMessage
  [:map {:closed true}
   [:type    [:= :rpc/push]]
   [:fn-id   FnId]
   [:args    [:vector :any]]
   [:push-id {:optional true} :string]
   [:ack?    {:optional true} :boolean]])

(def ErrorMessage
  [:map {:closed true}
   [:type    [:= :rpc/error]]
   [:code    [:enum
              :error/session-expired
              :error/auth-failed
              :error/cap-not-found
              :error/fn-not-found
              :error/rate-limited
              :error/server-fault
              :error/version-mismatch
              :error/cyclic-dep
              :error/invalid-message]]
   [:message :string]
   [:data    {:optional true} :any]
   [:qid     {:optional true} QuestionId]])

(def PongMessage
  [:map {:closed true}
   [:type  [:= :rpc/pong]]
   [:token {:optional true} :string]])

(def ServerMessage
  [:multi {:dispatch :type}
   [:rpc/welcome WelcomeMessage]
   [:rpc/return  ReturnMessage]
   [:rpc/push    PushMessage]
   [:rpc/error   ErrorMessage]
   [:rpc/pong    PongMessage]])

;; =============================================================================
;; Validation Helpers
;; =============================================================================

(def validate-client-msg (m/validator ClientMessage))
(def validate-server-msg (m/validator ServerMessage))

(def explain-client-msg (m/explainer ClientMessage))
(def explain-server-msg (m/explainer ServerMessage))
